import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider
} from '@mui/material';
import {
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Store as StoreIcon,
    AccessTime as AccessTimeIcon,
    Person as PersonIcon,
    VideoCall as VideoCallIcon
} from '@mui/icons-material';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import * as userAppointmentActions from '../../actions/user/user.appointments';
import * as sellerAppointmentActions from '../../actions/seller/seller.appointments';
import {openGoogleMeet} from "../../helpers/utils";

const AppointmentList = ({ 
    userType = 'user', 
    storeId = null, 
    appointments: externalAppointments = null,
    loading: externalLoading = false,
    pagination: externalPagination = null,
    onRefresh = null,
    hideFilter = false,
    externalStatusFilter = null,
    onStatusFilterChange = null
}) => {
    const dispatch = useDispatch();
    
    // Redux state
    const userAppointmentState = useSelector(state => state.appointments || {});
    const sellerAppointmentState = useSelector(state => state.sellerAppointments || {});
    
    // Choose the appropriate state based on userType
    const appointmentState = userType === 'store' ? sellerAppointmentState : userAppointmentState;
    const { 
        actionLoading: reduxActionLoading, 
        actionSuccess, 
        actionError
    } = appointmentState;
    
    // Get specific success states (only available for seller)
    const confirmSuccess = userType === 'store' ? appointmentState.confirmSuccess : false;
    const cancelSuccess = userType === 'store' ? appointmentState.cancelSuccess : false;
    
    
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [cancellationReason, setCancellationReason] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [internalStatusFilter, setInternalStatusFilter] = useState('');
    
    // Use external filter if provided, otherwise use internal filter
    const statusFilter = externalStatusFilter !== null ? externalStatusFilter : internalStatusFilter;
    const setStatusFilter = onStatusFilterChange || setInternalStatusFilter;
    const [userTimezone, setUserTimezone] = useState('Asia/Kolkata');
    
    useEffect(() => {
        // Set user timezone, default to India timezone
        const detectedTimezone = moment.tz.guess();
        const defaultTimezone = detectedTimezone && moment.tz.zone(detectedTimezone) ? detectedTimezone : 'Asia/Kolkata';
        setUserTimezone(defaultTimezone);
    }, []);
    
    // Handle action success
    useEffect(() => {
        if (actionSuccess) {
            toast.success('Action completed successfully');
            setCancelDialogOpen(false);
            setConfirmDialogOpen(false);
            setCancellationReason('');
            setSelectedAppointment(null);
            
            if (externalAppointments && onRefresh) {
                onRefresh();
            } else {
                fetchAppointments();
            }
            
            // Clear success state
            const actions = userType === 'store' ? sellerAppointmentActions : userAppointmentActions;
            if (actions.clearAppointmentSuccess) {
                dispatch(actions.clearAppointmentSuccess());
            }
        }
    }, [actionSuccess, dispatch, userType, externalAppointments, onRefresh]);
    
    // Handle specific confirm success
    useEffect(() => {
        if (confirmSuccess) {
            setConfirmDialogOpen(false);
            setSelectedAppointment(null);
            toast.success('Appointment confirmed successfully');
            
            if (externalAppointments && onRefresh) {
                onRefresh();
            } else {
                fetchAppointments();
            }
            
            // Clear success state
            const actions = userType === 'store' ? sellerAppointmentActions : userAppointmentActions;
            if (actions.clearAppointmentSuccess) {
                dispatch(actions.clearAppointmentSuccess());
            }
        }
    }, [confirmSuccess, dispatch, userType, externalAppointments, onRefresh]);
    
    // Handle specific cancel success
    useEffect(() => {
        if (cancelSuccess) {
            setCancelDialogOpen(false);
            setSelectedAppointment(null);
            setCancellationReason('');
            toast.success('Appointment cancelled successfully');
            
            if (externalAppointments && onRefresh) {
                onRefresh();
            } else {
                fetchAppointments();
            }
            
            // Clear success state
            const actions = userType === 'store' ? sellerAppointmentActions : userAppointmentActions;
            if (actions.clearAppointmentSuccess) {
                dispatch(actions.clearAppointmentSuccess());
            }
        }
    }, [cancelSuccess, dispatch, userType, externalAppointments, onRefresh]);
    
    // Handle action error
    useEffect(() => {
        if (actionError) {
            toast.error(actionError.message || 'Error performing action');
        }
    }, [actionError]);
    
    useEffect(() => {
        // Only fetch appointments if external appointments are not provided
        if (!externalAppointments) {
            fetchAppointments();
        }
    }, [currentPage, statusFilter, storeId, externalAppointments]);
    
    const fetchAppointments = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let url = '';
            
            if (userType === 'store') {
                url = `/api/v1/sellers/appointments?page=${currentPage}&limit=10`;
            } else {
                url = `/api/v1/users/appointments/my-appointments?page=${currentPage}&limit=10&user_timezone=${userTimezone}`;
            }
            
            if (statusFilter) {
                url += `&status=${statusFilter}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            const data = await response.json();
            
            if (data.success) {
                setAppointments(data.data.appointments);
                setTotalPages(data.data.pagination.total_pages);
            } else {
                toast.error(data.message || 'Error fetching appointments');
            }
        } catch (error) {
            console.error('Error fetching appointments:', error);
            toast.error('Error fetching appointments');
        } finally {
            setLoading(false);
        }
    };
    
    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled':
                return 'warning';
            case 'confirmed':
                return 'success';
            case 'cancelled':
                return 'error';
            case 'completed':
                return 'info';
            case 'no_show':
                return 'error';
            case 'missed':
                return 'error';
            case 'no_response':
                return 'error';
            default:
                return 'default';
        }
    };
    
    const getStatusIcon = (status) => {
        switch (status) {
            case 'scheduled':
                return <ScheduleIcon />;
            case 'confirmed':
                return <CheckCircleIcon />;
            case 'cancelled':
                return <CancelIcon />;
            case 'completed':
                return <CheckCircleIcon />;
            case 'no_show':
                return <CancelIcon />;
            case 'missed':
                return <CancelIcon />;
            case 'no_response':
                return <CancelIcon />;
            default:
                return <ScheduleIcon />;
        }
    };
    
    const handleCancelAppointment = (appointment) => {
        setSelectedAppointment(appointment);
        setCancelDialogOpen(true);
    };
    
    const handleConfirmAppointment = (appointment) => {
        setSelectedAppointment(appointment);
        setConfirmDialogOpen(true);
    };
    
    const confirmCancellation = () => {
        if (!selectedAppointment) return;
        
        const actions = userType === 'store' ? sellerAppointmentActions : userAppointmentActions;
        dispatch(actions.cancelAppointment(selectedAppointment._id, cancellationReason.trim()));
    };
    
    const confirmAppointmentAction = () => {
        if (!selectedAppointment) return;
        
        if (userType === 'store') {
            dispatch(sellerAppointmentActions.confirmAppointment(selectedAppointment._id));
        }
        // Users cannot confirm appointments, only sellers can
    };
    
    const canCancelAppointment = (appointment) => {
        return ['scheduled', 'confirmed'].includes(appointment.status) &&
               moment(appointment.start_time).isAfter(moment());
    };
    
    // Get display status - shows "missed" for sellers or "no_response" for users on unconfirmed past appointments
    const getDisplayStatus = (appointment) => {
        // Use backend-provided display_status if available, otherwise compute it
        if (appointment.display_status) {
            return appointment.display_status;
        }
        
        if (appointment.status === 'scheduled' && moment(appointment.start_time).isBefore(moment())) {
            // For sellers, show "missed", for users show "no_response"
            return userType === 'store' ? 'missed' : 'no_response';
        }
        return appointment.status;
    };

    const canConfirmAppointment = (appointment) => {
        const isStore = userType === 'store';
        const isScheduled = appointment.status === 'scheduled';
        const isFuture = moment(appointment.start_time).isAfter(moment());
        
        return isStore && isScheduled && isFuture;
    };
    
    const formatAppointmentTime = (appointment) => {
        if (userType === 'store') {
            return appointment.display_time_store || moment(appointment.start_time).format('MMMM Do, YYYY [at] h:mm A');
        } else {
            return appointment.display_times?.user_time || moment(appointment.start_time).format('MMMM Do, YYYY [at] h:mm A');
        }
    };
    
    // Use external data if provided, otherwise use internal state
    let displayAppointments = externalAppointments || appointments;
    
    // Filter appointments based on display status (including computed "missed" status)
    if (statusFilter) {
        displayAppointments = displayAppointments.filter(appointment => {
            const displayStatus = getDisplayStatus(appointment);
            return displayStatus === statusFilter;
        });
    }
    
    const displayLoading = externalAppointments ? externalLoading : loading;
    const displayPagination = externalPagination || { current_page: currentPage, total_pages: totalPages };
    
    if (displayLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Box>
            {!hideFilter && (
                <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Typography variant="h6">
                        {userType === 'store' ? 'Store Appointments' : 'My Appointments'}
                    </Typography>
                    
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            label="Filter by Status"
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="scheduled">Scheduled</MenuItem>
                            <MenuItem value="confirmed">Confirmed</MenuItem>
                            <MenuItem value="cancelled">Cancelled</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            {userType === 'store' && (
                                <MenuItem value="missed">Missed</MenuItem>
                            )}
                            {userType === 'user' && (
                                <MenuItem value="no_response">No Response</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                </Box>
            )}
            
            {displayAppointments.length === 0 ? (
                <Alert severity="info">
                    {statusFilter ? 
                        `No ${statusFilter} appointments found.` : 
                        'No appointments found.'
                    }
                </Alert>
            ) : (
                <Grid container spacing={2}>
                    {displayAppointments.map((appointment) => (
                        <Grid item xs={12} key={appointment._id}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Box>
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <Chip
                                                    icon={getStatusIcon(getDisplayStatus(appointment))}
                                                    label={getDisplayStatus(appointment).charAt(0).toUpperCase() + getDisplayStatus(appointment).slice(1)}
                                                    color={getStatusColor(getDisplayStatus(appointment))}
                                                    size="small"
                                                />
                                            </Box>
                                            
                                            <Typography variant="h6" gutterBottom>
                                                {userType === 'store' ? (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <PersonIcon fontSize="small" />
                                                        {appointment.user_id?.name || 'Unknown User'}
                                                    </Box>
                                                ) : (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <StoreIcon fontSize="small" />
                                                        {appointment.store_id?.title || 'Unknown Store'}
                                                    </Box>
                                                )}
                                            </Typography>
                                            
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <AccessTimeIcon fontSize="small" color="action" />
                                                <Typography variant="body2" color="textSecondary">
                                                    {formatAppointmentTime(appointment)}
                                                </Typography>
                                            </Box>
                                            
                                            {userType === 'user' && appointment.display_times && (
                                                <Typography variant="caption" color="textSecondary" display="block">
                                                    Store time: {appointment.display_times.store_time}
                                                </Typography>
                                            )}
                                            
                                            {userType === 'store' && appointment.user_id?.country_code && (
                                                <Typography variant="body2" color="textSecondary">
                                                    Country Code: +{appointment.user_id.country_code}
                                                </Typography>
                                            )}
                                            
                                            {userType === 'store' && appointment.user_id?.mobile && (
                                                <Typography variant="body2" color="textSecondary">
                                                    Mobile: {appointment.user_id.mobile}
                                                </Typography>
                                            )}
                                            
                                            {appointment.notes && (
                                                <Box mt={1}>
                                                    <Typography variant="body2">
                                                        <strong>Notes:</strong> {appointment.notes}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {appointment.google_meet_link && appointment.status === 'confirmed' && (
                                                <Box mt={2}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        startIcon={<VideoCallIcon />}
                                                        onClick={() => openGoogleMeet(appointment.google_meet_link)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Join Google Meet
                                                    </Button>
                                                    <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                                                        Meeting link available after confirmation
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {appointment.cancelled_by && (
                                                <Box mt={1}>
                                                    <Typography variant="body2" color="error">
                                                        Cancelled by: {appointment.cancelled_by}
                                                        {appointment.cancellation_reason && ` - ${appointment.cancellation_reason}`}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            {canConfirmAppointment(appointment) && (
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    onClick={() => handleConfirmAppointment(appointment)}
                                                >
                                                    Confirm
                                                </Button>
                                            )}
                                            
                                            {canCancelAppointment(appointment) && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => handleCancelAppointment(appointment)}
                                                >
                                                    Cancel
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
            
            {displayPagination.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                        count={displayPagination.total_pages}
                        page={displayPagination.current_page}
                        onChange={(event, value) => {
                            if (externalAppointments && onRefresh) {
                                // For external data, call the refresh function
                                onRefresh(value);
                            } else {
                                // For internal data, update the current page
                                setCurrentPage(value);
                            }
                        }}
                        color="primary"
                    />
                </Box>
            )}
            
            {/* Cancel Appointment Dialog */}
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cancel Appointment</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to cancel this appointment?
                    </Typography>
                    
                    {selectedAppointment && (
                        <Box mb={2}>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Time:</strong> {formatAppointmentTime(selectedAppointment)}
                            </Typography>
                            {userType === 'user' && selectedAppointment.store_id && (
                                <Typography variant="body2" color="textSecondary">
                                    <strong>Store:</strong> {selectedAppointment.store_id.title}
                                </Typography>
                            )}
                            {userType === 'store' && selectedAppointment.user_id && (
                                <Typography variant="body2" color="textSecondary">
                                    <strong>Customer:</strong> {selectedAppointment.user_id.name}
                                </Typography>
                            )}
                        </Box>
                    )}
                    
                    <TextField
                        fullWidth
                        label="Reason for cancellation (optional)"
                        multiline
                        rows={3}
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                        placeholder="Please provide a reason for cancelling this appointment..."
                        inputProps={{ maxLength: 200 }}
                        helperText={`${cancellationReason.length}/200 characters`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)} disabled={reduxActionLoading}>
                        Keep Appointment
                    </Button>
                    <Button 
                        onClick={confirmCancellation} 
                        color="error" 
                        variant="contained"
                        disabled={reduxActionLoading}
                        startIcon={reduxActionLoading ? <CircularProgress size={20} /> : null}
                    >
                        {reduxActionLoading ? 'Cancelling...' : 'Cancel Appointment'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Confirm Appointment Dialog */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Appointment</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to confirm this appointment?
                    </Typography>
                    
                    {selectedAppointment && (
                        <Box mb={2}>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Customer:</strong> {selectedAppointment.user_id?.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Time:</strong> {formatAppointmentTime(selectedAppointment)}
                            </Typography>
                            {selectedAppointment.notes && (
                                <Typography variant="body2" color="textSecondary">
                                    <strong>Notes:</strong> {selectedAppointment.notes}
                                </Typography>
                            )}
                        </Box>
                    )}
                    
                    <Alert severity="info">
                        Confirming this appointment will:
                        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                            <li>Mark the appointment as confirmed</li>
                            <li>Create a Google Meet link for the video call</li>
                            <li>Notify the customer with meeting details</li>
                        </ul>
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} disabled={reduxActionLoading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmAppointmentAction} 
                        color="success" 
                        variant="contained"
                        disabled={reduxActionLoading}
                        startIcon={reduxActionLoading ? <CircularProgress size={20} /> : null}
                    >
                        {reduxActionLoading ? 'Confirming...' : 'Confirm Appointment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AppointmentList;
